import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslations from './locales/en.json';
import frTranslations from './locales/fr.json';
import esTranslations from './locales/es.json';
import nlTranslations from './locales/nl.json';

// Temporarily disabled strict typing for i18next to avoid type conflicts
// declare module 'react-i18next' {
//   interface CustomTypeOptions {
//     defaultNS: 'translation';
//     resources: {
//       translation: Translations;
//     };
//   }
// }

export const resources = {
  en: {
    translation: enTranslations
  },
  fr: {
    translation: frTranslations
  },
  es: {
    translation: esTranslations
  },
  nl: {
    translation: nlTranslations
  }
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'es', 'nl'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'sessionStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      lookupSessionStorage: 'i18nextLng',
      caches: ['localStorage', 'sessionStorage'],
      cookieMinutes: 60 * 24 * 365
    }
  });

// Fonction pour changer la langue de manière persistante
export const changeLanguage = (lang: string) => {
  i18n.changeLanguage(lang);
  localStorage.setItem('i18nextLng', lang);
  document.documentElement.lang = lang;
};

/**
 * Forçage manuel de la langue (feature `language_override`).
 *
 * Quand une langue est "forcée", elle prime sur le mécanisme automatique :
 * l'intercepteur de réponse axios (cf. services/api.ts) cesse d'appliquer le
 * header `x-user-language` renvoyé par le backend. La préférence est persistée
 * dans `sm_forced_lang` (distincte de `i18nextLng`, qui reste la langue
 * effective lue par le LanguageDetector au boot).
 *
 * Repasser en `null` (option "Auto") supprime l'override : la langue courante
 * est conservée et le backend reprend la main à la prochaine réponse API.
 */
export const FORCED_LANGUAGE_KEY = 'sm_forced_lang';

function isSupportedLanguage(lang: string): boolean {
  const supported = i18n.options.supportedLngs;
  return Array.isArray(supported) && supported.includes(lang);
}

export const getForcedLanguage = (): string | null => {
  try {
    const v = window.localStorage.getItem(FORCED_LANGUAGE_KEY);
    return v && isSupportedLanguage(v) ? v : null;
  } catch {
    return null;
  }
};

export const setForcedLanguage = (lang: string | null) => {
  // Validation défensive : on n'écrit/applique qu'une langue supportée.
  if (lang && !isSupportedLanguage(lang)) {
    return;
  }
  try {
    if (lang) {
      window.localStorage.setItem(FORCED_LANGUAGE_KEY, lang);
    } else {
      window.localStorage.removeItem(FORCED_LANGUAGE_KEY);
    }
  } catch {
    /* SSR / private mode — ignore */
  }
  if (lang) {
    changeLanguage(lang);
  }
};

// Au boot, si une langue est forcée, on la (ré)applique pour neutraliser toute
// course avec le header serveur au premier rendu.
const forcedAtBoot = getForcedLanguage();
if (forcedAtBoot && forcedAtBoot !== i18n.language) {
  changeLanguage(forcedAtBoot);
}

export default i18n;